import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { kpiApi } from '../lib/api';

/**
 * Which imported numbers are scored by a KPI, and which nothing reads.
 *
 * Every month the three apps hand over a few hundred figures. A minority are
 * bound to a KPI, and finding out WHICH minority meant opening every KPI one
 * at a time — so a number the business wanted scored could sit unbound for
 * months with nothing saying so.
 *
 * READ-ONLY. Nothing here creates a binding or enables a KPI: deciding that an
 * unscored number SHOULD be scored is a judgement about the business, and
 * several of these are operational or informational and unscored on purpose.
 * The screen says so rather than presenting every row as a defect.
 *
 * All classification happens on the server, where the bindings, the routes and
 * the built figures already live. This renders what it is told.
 */

const STATUS = {
  used: { label: 'Scored', fg: '#15803D', bg: '#E7F7EE',
    help: 'An active KPI reads this number directly.' },
  indirect: { label: 'Scored indirectly', fg: '#1E3A8A', bg: '#E8EFFD',
    help: 'Reached through a route, a built figure, or as the source’s second '
        + 'name for a number a KPI already reads.' },
  needs_review: { label: 'Needs review', fg: '#A16207', bg: '#FEF6E7',
    help: 'The application reads this number somewhere, but no KPI scores it. '
        + 'Could be an operational screen; could be a binding somebody meant to make.' },
  inactive_only: { label: 'Retired KPI only', fg: '#64748B', bg: '#F1F3F7',
    help: 'Only a retired KPI referenced it, which earns no marks.' },
  unused: { label: 'Not scored', fg: '#B91C1C', bg: '#FDE9EF',
    help: 'No active KPI reads it, by any route.' },
};

const SOURCES = [['', 'All sources'], ['ops', 'Operations'],
                 ['maint', 'Machine Maintenance'], ['npd', 'NPD'], ['hr', 'HR software']];

function Tile({ label, value, tone = 'plain', help }) {
  const tones = {
    plain: 'bg-white border-[#E6E9F0] text-[#11182B]',
    good: 'bg-[#F2FBF6] border-[#CDEBDB] text-[#15803D]',
    warn: 'bg-[#FFFBF2] border-[#FBD9A8] text-[#A16207]',
    bad: 'bg-[#FFF5F7] border-[#FBC9D6] text-[#B91C1C]',
  };
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 ${tones[tone]}`} title={help}>
      <div className="text-[10px] uppercase tracking-wide text-[#8B8FA8]">{label}</div>
      <div className="text-xl font-semibold tabular-nums leading-tight">{value}</div>
    </div>
  );
}

export default function SourceUsageAudit({ source }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [mod, setMod] = useState(source || '');
  const [status, setStatus] = useState('unused');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await kpiApi.sourceUsage({ source: mod || undefined, limit: 1000 });
      setData(r.data);
    } catch (e) {
      // 403 is a different thing from a failure and reads differently.
      setErr({ status: e?.response?.status,
               detail: e?.response?.data?.detail || 'Could not read the audit' });
      setData(null);
    } finally { setBusy(false); }
  }, [mod]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.points || [];
    const needle = q.trim().toLowerCase();
    return all.filter((r) => (!status || r.status === status)
      && (!needle || (r.path || '').toLowerCase().includes(needle)
                  || (r.label || '').toLowerCase().includes(needle)));
  }, [data, status, q]);

  const s = data?.summary;
  const counts = useMemo(() => {
    const c = {};
    (data?.points || []).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [data]);

  if (err) {
    return (
      <div className="rounded-xl border border-[#FBC9D6] bg-[#FFF5F7] px-4 py-3"
        data-testid="usage-audit-error">
        <p className="text-sm font-medium text-[#B91C1C]">
          {err.status === 403 ? 'You do not have permission to see this audit'
                              : 'The audit could not be read'}
        </p>
        <p className="text-[12px] text-[#7A4A12] mt-0.5">
          {err.status === 403
            ? 'It lists how every imported number is bound, which is administrative detail. '
              + 'Ask an administrator for KPI edit access.'
            : err.detail}
        </p>
        {err.status !== 403 && (
          <button onClick={load} className="text-[12px] text-[#2563EB] hover:underline mt-1.5">
            Try again
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="usage-audit">
      <div className="flex items-baseline gap-2 flex-wrap mb-3">
        <h3 className="font-medium text-[#11182B]">Which numbers are scored</h3>
        <p className="text-sm text-[#5A6377]">
          Imported figures, and the KPIs that read them.
        </p>
        <button onClick={load} disabled={busy}
          className="ml-auto text-[12px] text-[#2563EB] hover:underline"
          data-testid="usage-audit-refresh">{busy ? 'Reading…' : 'Refresh'}</button>
      </div>

      {busy && !data && (
        <p className="text-sm text-[#98A1B2]" data-testid="usage-audit-loading">
          Reading every binding…
        </p>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            <Tile label="Source numbers" value={s.source_points}
              help="One canonical path per source. Built figures and second spellings are not counted here." />
            <Tile label="Active KPIs" value={s.active_kpis}
              help={`${s.active_kpis_with_a_binding} of them read a source number; the rest are typed in by hand.`} />
            <Tile label="Scored" value={s.used} tone="good"
              help={STATUS.used.help} />
            <Tile label="Scored indirectly" value={s.indirect}
              help={STATUS.indirect.help} />
            <Tile label="Needs review" value={s.needs_review} tone="warn"
              help={STATUS.needs_review.help} />
            <Tile label="Coverage" value={`${s.coverage_pct}%`}
              tone={s.coverage_pct >= 60 ? 'good' : 'plain'}
              help="Scored plus scored-indirectly, over all source numbers." />
          </div>

          {/* The alert, and the caveat that keeps it honest. A screen that
              presents every unscored number as a defect gets ignored by the
              second visit, because most of them are not. */}
          <div className={`rounded-xl border px-4 py-3 mb-3 ${s.unused
            ? 'border-[#FBD9A8] bg-[#FFFBF2]' : 'border-[#CDEBDB] bg-[#F2FBF6]'}`}
            data-testid="usage-audit-alert">
            {s.unused ? (
              <>
                <p className="text-sm font-medium text-[#B14A0E]">
                  {s.unused} imported number{s.unused === 1 ? ' is' : 's are'} not scored by any KPI
                </p>
                <p className="text-[12px] text-[#7A4A12] mt-0.5">
                  That is not automatically a fault. A source publishes everything it has,
                  and plenty of it is operational or informational — a record count, a
                  timestamp, a figure kept for a screen rather than for an appraisal. Use
                  this to find the ones that SHOULD be scored and are not.
                </p>
              </>
            ) : (
              <p className="text-sm font-medium text-[#15803D]">
                Every imported number is read by a KPI.
              </p>
            )}
          </div>
        </>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select value={mod} onChange={(e) => setMod(e.target.value)}
          data-testid="usage-audit-source"
          className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm text-[#5A6377]">
          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search a path or a name…" data-testid="usage-audit-search"
          className="border border-[#E6E9F0] rounded-lg px-3 py-1.5 text-sm max-w-xs outline-none focus:border-[#2563EB]" />
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(STATUS).map(([k, v]) => (
            <button key={k} onClick={() => setStatus(status === k ? '' : k)}
              title={v.help} data-testid={`usage-audit-status-${k}`}
              aria-pressed={status === k}
              className={`text-[11px] rounded-full px-2.5 py-0.5 border ${status === k
                ? 'border-[#2563EB] font-medium' : 'border-[#E6E9F0]'}`}
              style={status === k ? { background: v.bg, color: v.fg } : {}}>
              {v.label} {counts[k] || 0}
            </button>
          ))}
        </div>
      </div>

      {data && !rows.length && (
        <p className="text-sm text-[#98A1B2] py-6 text-center" data-testid="usage-audit-empty">
          {status ? `No numbers are "${STATUS[status]?.label}"` : 'Nothing matches'}
          {q ? ` matching “${q}”.` : '.'}
        </p>
      )}

      {!!rows.length && (
        <div className="rounded-xl border border-[#E6E9F0] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#98A1B2] text-[11px] uppercase">
                <th className="text-left font-semibold px-3 py-2">Number</th>
                <th className="text-left font-semibold px-3 py-2 w-40">Status</th>
                <th className="text-left font-semibold px-3 py-2">Read by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS[r.status] || STATUS.unused;
                return (
                  <tr key={r.id} className="border-t border-[#F2F4F8] align-top"
                    data-testid={`usage-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="text-[#11182B]">{r.label}</div>
                      <div className="font-mono text-[10.5px] text-[#B4B9CC]">{r.path}</div>
                      <div className="flex gap-2 flex-wrap mt-0.5">
                        {r.hidden && (
                          <span className="text-[10px] text-[#64748B]">hidden from the picker</span>
                        )}
                        {/* Availability is a SEPARATE question from usage: a
                            number can be bound and empty, or present and
                            unread. Conflating them hides both. */}
                        {!r.has_any_value && (
                          <span className="text-[10px] text-[#B14A0E]">no figure this month</span>
                        )}
                        {r.missing_since && (
                          <span className="text-[10px] text-[#B91C1C]">no longer supplied</span>
                        )}
                        {r.alias_of && (
                          <span className="text-[10px] text-[#64748B]">
                            also published as {r.alias_of}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{ background: st.bg, color: st.fg }} title={st.help}>
                        {st.label}
                      </span>
                      {!!(r.via || []).length && (
                        <div className="text-[10.5px] text-[#98A1B2] mt-0.5">
                          via {r.via.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-[#5A6377]">
                      {r.used_by?.length ? r.used_by.join(', ') : (
                        <span className="text-[#C9CFDC]">
                          {r.status === 'needs_review'
                            ? 'application code, but no KPI'
                            : 'nothing'}
                        </span>
                      )}
                      {!!r.used_by_inactive?.length && (
                        <div className="text-[11px] text-[#B14A0E] mt-0.5">
                          retired KPI only: {r.used_by_inactive.join(', ')}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The definitions the numbers above depend on. "171 unused" means
          nothing without knowing what was counted and what was left out. */}
      {!!data?.counting_rules?.length && (
        <details className="mt-3">
          <summary className="text-[12px] text-[#2563EB] cursor-pointer">
            How these are counted
          </summary>
          <ul className="mt-1.5 text-[11.5px] text-[#5A6377] list-disc pl-5 space-y-0.5">
            {data.counting_rules.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}
